import React, { useEffect, useState } from 'react';
import { BarChart3, Users, TrendingUp, Eye, ArrowDownRight, RefreshCw, Globe } from 'lucide-react';

const AnalyticsTab = ({ BACKEND_URL }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/analytics/summary`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [BACKEND_URL]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center text-[#8d6e63]">
        Loading analytics…
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center text-red-600">
        Failed to load analytics: {error}
      </div>
    );
  }

  const v = data.visitors;
  const f = data.funnel;
  const maxDaily = Math.max(1, ...data.daily.map((d) => d.visitors));

  const KpiCard = ({ label, value, Icon, accent = '#1a4d2e' }) => (
    <div className="bg-white border border-[#e5ddd5] rounded-xl p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-[#8d6e63] font-semibold mb-1">{label}</p>
          <p className="text-3xl font-bold text-[#1a4d2e] font-mono">{value.toLocaleString()}</p>
        </div>
        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${accent}1a` }}>
          <Icon className="w-5 h-5" style={{ color: accent }} />
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl text-[#1a4d2e] flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-[#ff6b00]" />
            Visitor Analytics
          </h2>
          <p className="text-sm text-[#8d6e63] mt-1">In-app visitor counter — unique sessions across the public site.</p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-[#e5ddd5] rounded-lg hover:bg-[#f7f5f1] text-[#1a4d2e] font-medium text-sm"
          data-testid="analytics-refresh-btn"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Today" value={v.today} Icon={Eye} accent="#ff6b00" />
        <KpiCard label="Last 7 days" value={v.week} Icon={Users} accent="#1a4d2e" />
        <KpiCard label="Last 30 days" value={v.month} Icon={TrendingUp} accent="#2563eb" />
        <KpiCard label="All time" value={v.all_time} Icon={Globe} accent="#7c3aed" />
      </div>

      {/* Daily visitors chart */}
      <div className="bg-white border border-[#e5ddd5] rounded-xl p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-[#1a4d2e] mb-4 uppercase tracking-wider">Daily Visitors (last 30 days)</h3>
        {data.daily.length === 0 ? (
          <p className="text-sm text-[#8d6e63]">No visits yet — share the site and you'll see numbers here.</p>
        ) : (
          <div className="flex items-end gap-1 h-32">
            {data.daily.map((d) => {
              const h = Math.max(4, (d.visitors / maxDaily) * 100);
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center group relative" data-testid={`bar-${d.date}`}>
                  <div className="w-full bg-[#ff6b00]/80 hover:bg-[#ff6b00] rounded-t transition-all" style={{ height: `${h}%` }} />
                  <span className="absolute -top-7 hidden group-hover:block text-xs bg-[#1a4d2e] text-white px-2 py-0.5 rounded whitespace-nowrap z-10">
                    {d.date}: {d.visitors}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Funnel */}
      <div className="bg-white border border-[#e5ddd5] rounded-xl p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-[#1a4d2e] mb-1 uppercase tracking-wider">Booking Funnel (last 30 days)</h3>
        <p className="text-xs text-[#8d6e63] mb-4">How many visitors progress through each step.</p>

        <div className="space-y-3">
          {[
            { label: 'Visitors', value: f.visitors, pct: 100, color: '#1a4d2e' },
            { label: 'Registered (Light Gate)', value: f.registered, pct: f.registered_pct, color: '#2563eb' },
            { label: 'Quote Calculated', value: f.quoted, pct: f.quoted_pct, color: '#ff6b00' },
            { label: 'Booking Submitted', value: f.booked, pct: f.booked_pct, color: '#16a34a' },
          ].map(({ label, value, pct, color }) => (
            <div key={label}>
              <div className="flex justify-between items-center text-sm mb-1">
                <span className="font-medium text-[#1a4d2e]">{label}</span>
                <span className="font-mono text-[#4a4a4a]">
                  {value.toLocaleString()} {label !== 'Visitors' && <span className="text-xs text-[#8d6e63]">({pct}%)</span>}
                </span>
              </div>
              <div className="h-3 bg-[#f4f4f0] rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(2, pct)}%`, background: color }} />
              </div>
            </div>
          ))}
        </div>

        {f.quoted > 0 && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-sm">
            <ArrowDownRight className="w-4 h-4 text-green-700" />
            <span className="text-green-800">
              Quote → Booking conversion: <strong>{f.quote_to_book_pct}%</strong>
              {f.quote_to_book_pct >= 30 ? ' — great!' : ' — try following up unsubmitted drafts.'}
            </span>
          </div>
        )}
      </div>

      {/* Top pages */}
      <div className="bg-white border border-[#e5ddd5] rounded-xl p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-[#1a4d2e] mb-4 uppercase tracking-wider">Top Pages (last 30 days)</h3>
        {data.top_pages.length === 0 ? (
          <p className="text-sm text-[#8d6e63]">No data yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-[#8d6e63] uppercase tracking-wider">
              <tr>
                <th className="text-left pb-2">Path</th>
                <th className="text-right pb-2">Views</th>
                <th className="text-right pb-2">Unique Visitors</th>
              </tr>
            </thead>
            <tbody>
              {data.top_pages.map((p) => (
                <tr key={p.path} className="border-t border-[#f4f4f0]">
                  <td className="py-2 text-[#1a4d2e] font-mono">{p.path}</td>
                  <td className="py-2 text-right font-mono">{p.views.toLocaleString()}</td>
                  <td className="py-2 text-right font-mono text-[#ff6b00]">{p.unique.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-xs text-[#8d6e63] text-center">
        Generated {new Date(data.generated_at).toLocaleString()} · Sessions are counted by unique browser sessions, not unique humans.
        {process.env.REACT_APP_GA4_ID ? ' · Google Analytics 4 also active.' : ' · Add REACT_APP_GA4_ID to enable Google Analytics 4.'}
      </p>
    </div>
  );
};

export default AnalyticsTab;
